import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { isTokenExpired } from '../lib/parseJwt';
import {
  AUTH_ATTEMPTED_KEY,
  REFRESH_CALLED_KEY,
  JWT_CACHE_KEY,
  REFRESH_URL,
  ERROR_AUTH_SERVICE,
  ERROR_CONNECTION,
  ERROR_AUTH_FAILED,
  AUTH_ERROR_INVALID_GRANT,
  AUTH_ERROR_REFRESH_TOKEN,
  HTTP_UNAUTHORIZED,
  HTTP_FORBIDDEN,
  buildAuthUrl,
} from '../constants/auth';

interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  jwt: string | null;
  error: string | null;
}

interface AuthResponse {
  token?: string;
  profile?: unknown;
  providerInfo?: unknown;
  backstageIdentity?: {
    token?: string;
  };
  jwt?: {
    token: string;
  };
  error?: string;
  error_description?: string;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Owns the one auth check, refresh interval and refresh request for the whole app;
 * every useAuth() caller reads this provider's state.
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    jwt: null,
    error: null,
  });

  useEffect(() => {
    const redirectToLogin = () => {
      const authAttempted = sessionStorage.getItem(AUTH_ATTEMPTED_KEY);

      // Clear cached JWT on redirect to login
      sessionStorage.removeItem(JWT_CACHE_KEY);

      // If we've already attempted login once, show error to prevent loop
      if (authAttempted) {
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          jwt: null,
          error: ERROR_AUTH_FAILED,
        });
        return;
      }

      // Mark that we've attempted auth and redirect to login
      sessionStorage.setItem(AUTH_ATTEMPTED_KEY, 'true');
      const currentPath = window.location.pathname;
      const authUrl = buildAuthUrl(currentPath);
      window.location.href = authUrl;
    };

    const checkAuth = async () => {
      // Check for cached JWT first
      const cachedJwt = sessionStorage.getItem(JWT_CACHE_KEY);
      if (cachedJwt && !isTokenExpired(cachedJwt)) {
        // Use cached token if it's still valid
        setAuthState({
          isLoading: false,
          isAuthenticated: true,
          jwt: cachedJwt,
          error: null,
        });
        return;
      }

      // Clear expired or invalid cached token
      if (cachedJwt) {
        sessionStorage.removeItem(JWT_CACHE_KEY);
      }

      try {
        const response = await fetch(REFRESH_URL, {
          credentials: 'include',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (response.ok) {
          const data = await response.json() as AuthResponse;

          // Check for token in the refresh response. The endpoint returns it at the top
          // level ({ token }); keep backstageIdentity/jwt as fallbacks for other shapes.
          const token = data?.token || data?.backstageIdentity?.token || data?.jwt?.token;

          if (token) {
            // Successfully authenticated - cache token and clear flags
            sessionStorage.setItem(JWT_CACHE_KEY, token);
            sessionStorage.removeItem(AUTH_ATTEMPTED_KEY);
            sessionStorage.removeItem(REFRESH_CALLED_KEY);
            setAuthState({
              isLoading: false,
              isAuthenticated: true,
              jwt: token,
              error: null,
            });
          } else {
            // No token in response - trigger login
            redirectToLogin();
          }
        } else {
          // Check if it's an auth-related error. The body may not be JSON (e.g. an ingress 502),
          // and the status must still reach the error message.
          const data = await response.json().catch(() => ({})) as AuthResponse;
          const isAuthError = data?.error === AUTH_ERROR_INVALID_GRANT ||
                            data?.error_description?.toLowerCase().includes(AUTH_ERROR_REFRESH_TOKEN) ||
                            response.status === HTTP_UNAUTHORIZED ||
                            response.status === HTTP_FORBIDDEN;

          if (isAuthError) {
            // Invalid grant or refresh token failure - trigger login
            redirectToLogin();
          } else {
            // Other error - show error message
            setAuthState({
              isLoading: false,
              isAuthenticated: false,
              jwt: null,
              error: `${ERROR_AUTH_SERVICE}: ${response.status}`,
            });
          }
        }
      } catch {
        // Network or other error - show error message
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          jwt: null,
          error: ERROR_CONNECTION,
        });
      }
    };

    void checkAuth();

    // Periodically check if the cached token has expired and refresh proactively.
    // This prevents MFEs from using a stale JWT after the user has been idle.
    // Use a shorter interval in development to make it easier to test.
    const isDev = process.env.NODE_ENV === 'development';
    const TOKEN_CHECK_INTERVAL_MS = isDev ? 10_000 : 60_000;
    const interval = setInterval(() => {
      const cachedJwt = sessionStorage.getItem(JWT_CACHE_KEY);
      if (cachedJwt && isTokenExpired(cachedJwt)) {
        void checkAuth();
      }
    }, TOKEN_CHECK_INTERVAL_MS);

    // Dev helper: call window.__expireAuth() in the browser console to simulate
    // session expiry and trigger the refresh flow immediately.
    if (isDev) {
      (window as Window & { __expireAuth?: () => void }).__expireAuth = () => {
        sessionStorage.removeItem(JWT_CACHE_KEY);
        void checkAuth();
      };
    }

    return () => clearInterval(interval);
  }, []);

  return <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>;
};
